// src/components/resident/MyDepositCard.tsx — the resident's own deposit on My home (Task T): where it stands, the
// scheme and its certificate number, the prescribed information and the certificate to open, and the return. It sits on
// the portal's Rent page, so it wears the portal's card (components/tenant/kit).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, ShieldCheck } from 'lucide-react';
import { Badge, Money } from '../ui';
import { TCard, TFacts } from '../tenant/kit';
import { formatDate } from '../../lib/viz';
import { depositsApi, gbp } from '../../lib/deposits';
import { openStoredFile } from '../../lib/reservations';
import { formatPhone } from '../../lib/phone';

export function MyDepositCard({ tenancyId }: { tenancyId: string }) {
  const q = useQuery({ queryKey: ['deposit', 'mine'], queryFn: () => depositsApi.mine() });
  const [err, setErr] = useState<string | null>(null);
  const d = q.data?.find((x) => x.tenancyId === tenancyId) ?? q.data?.[0];
  if (!d) return null;
  const open = (url: string) => void openStoredFile(url).catch((e: Error) => setErr(e.message));
  const tone = d.status === 'RELEASED' ? 'good' : d.status === 'DISPUTED' ? 'warning' : d.certificateNumber ? 'good' : 'warning';
  return (
    <TCard title="Your deposit" sub="Where it is held, and how it comes back to you">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2"><Money minor={d.amountMinor} currency={d.currency} className="t-serif text-[34px] leading-none text-ink-900" /><Badge value={d.state} tone={tone} /></div>
        <TFacts items={[
            { k: 'Scheme', v: d.scheme ? d.scheme.name : 'Being chosen' },
            { k: 'Certificate', v: d.certificateNumber ? <span className="font-mono">{d.certificateNumber}</span> : d.protectBy ? `Protected by ${formatDate(d.protectBy)} at the latest` : '—' },
            { k: 'Protected on', v: d.protectedOn ? formatDate(d.protectedOn) : '—' },
            { k: 'Information given', v: d.prescribedInfoServedOn ? formatDate(d.prescribedInfoServedOn) : 'Not yet' },
        ]} />
        {(d.prescribedInfoUrl || d.certificateUrl) && (
          <div className="flex flex-wrap gap-2">
            {d.prescribedInfoUrl && <button type="button" onClick={() => open(d.prescribedInfoUrl!)} className="t-btn t-btn-soft t-btn-sm"><FileText className="h-4 w-4" /> Prescribed information</button>}
            {d.certificateUrl && <button type="button" onClick={() => open(d.certificateUrl!)} className="t-btn t-btn-soft t-btn-sm"><ShieldCheck className="h-4 w-4" /> Certificate</button>}
          </div>
        )}
        {(d.return.toYouMinor !== null || d.return.disputeReference) && (
          <div className="rounded-[16px] bg-bronze-50 px-4 py-3 text-[14px] leading-relaxed text-ink-800">
            {d.return.disputeReference ? <>With {d.scheme?.name ?? 'the scheme'}'s free dispute service, reference <b>{d.return.disputeReference}</b>. Its decision is binding on both of us.</>
              : d.return.returnedOn ? <>{gbp(d.return.toYouMinor, d.currency)} paid back to you on {formatDate(d.return.returnedOn)}.</>
                : <>We propose to return {gbp(d.return.toYouMinor, d.currency)}{d.return.deductions.length ? `, keeping ${d.return.deductions.map((x) => `${gbp(x.amountMinor, d.currency)} for ${x.reason.toLowerCase()}`).join(', ')}` : ''}.{d.return.agreedOn ? ' Agreed — the money is on its way.' : ' Reply to our letter to agree, or ask the scheme to decide.'}</>}
          </div>
        )}
        {d.scheme && <p className="text-[13px] leading-relaxed text-ink-500">Questions about the protection: {d.scheme.name}, {formatPhone(d.scheme.phone)}, {d.scheme.web}.</p>}
        {err && <p className="text-[13px] text-[#8A3F33]">{err}</p>}
      </div>
    </TCard>
  );
}
